const ANON_ID_STORAGE_KEY = 'qcl_anon_id';
const ANON_ID_COOKIE_KEY = 'qcl_anon_id';
const SESSION_ID_STORAGE_KEY = 'qcl_session_id';
const ANON_ID_MAX_AGE_SECONDS = 90 * 24 * 60 * 60;
const TRACKING_ID_PATTERN = /^[a-zA-Z0-9][a-zA-Z0-9_-]{5,127}$/;

interface TelemetryIdentity {
  anonId: string | null;
  sessionId: string | null;
  site: string;
}

const hasWindow = (): boolean => typeof window !== 'undefined';

const isValidTrackingId = (value: string | null | undefined): value is string => {
  if (!value) {
    return false;
  }

  return TRACKING_ID_PATTERN.test(value);
};

const generateTrackingId = (): string => {
  if (hasWindow() && window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }

  return `qcl_${Math.random().toString(36).slice(2, 12)}${Date.now().toString(36)}`;
};

const readCookie = (name: string): string | null => {
  if (!hasWindow() || !document.cookie) {
    return null;
  }

  const encodedName = encodeURIComponent(name);
  const cookiePart = document.cookie
    .split(';')
    .map((entry) => entry.trim())
    .find((entry) => entry.startsWith(`${encodedName}=`));

  if (!cookiePart) {
    return null;
  }

  const [, rawValue] = cookiePart.split('=');
  const decoded = rawValue ? decodeURIComponent(rawValue) : '';
  return decoded || null;
};

const isQuantcheckDomain = (hostname: string): boolean =>
  hostname === 'quantchecklabs.com' || hostname.endsWith('.quantchecklabs.com');

const writeCookie = (name: string, value: string, maxAgeSeconds: number): void => {
  if (!hasWindow()) {
    return;
  }

  const parts = [
    `${encodeURIComponent(name)}=${encodeURIComponent(value)}`,
    'Path=/',
    'SameSite=Lax',
    `Max-Age=${maxAgeSeconds}`,
  ];

  if (window.location.protocol === 'https:') {
    parts.push('Secure');
  }

  if (isQuantcheckDomain(window.location.hostname)) {
    parts.push('Domain=.quantchecklabs.com');
  }

  document.cookie = parts.join('; ');
};

const resolveSite = (): string => {
  if (!hasWindow()) {
    return 'unknown';
  }

  const host = window.location.hostname.toLowerCase();
  if (host === 'platform.quantchecklabs.com') {
    return 'platform';
  }

  if (host === 'www.quantchecklabs.com' || host === 'quantchecklabs.com') {
    return 'marketing';
  }

  if (host === 'localhost' || host === '127.0.0.1') {
    return 'local_dev';
  }

  return host || 'unknown';
};

const readFromStorage = (storage: Storage, key: string): string | null => {
  try {
    const value = storage.getItem(key);
    return isValidTrackingId(value) ? value : null;
  } catch {
    return null;
  }
};

const writeToStorage = (storage: Storage, key: string, value: string): void => {
  try {
    storage.setItem(key, value);
  } catch {
    // Ignore storage write failures (e.g. private browsing restrictions).
  }
};

const resolveAnonId = (): string | null => {
  if (!hasWindow()) {
    return null;
  }

  const cookieId = readCookie(ANON_ID_COOKIE_KEY);
  const storageId = readFromStorage(window.localStorage, ANON_ID_STORAGE_KEY);
  const resolved = isValidTrackingId(cookieId)
    ? cookieId
    : isValidTrackingId(storageId)
      ? storageId
      : generateTrackingId();

  writeToStorage(window.localStorage, ANON_ID_STORAGE_KEY, resolved);
  writeCookie(ANON_ID_COOKIE_KEY, resolved, ANON_ID_MAX_AGE_SECONDS);
  return resolved;
};

const resolveSessionId = (): string | null => {
  if (!hasWindow()) {
    return null;
  }

  const sessionId = readFromStorage(window.sessionStorage, SESSION_ID_STORAGE_KEY) || generateTrackingId();
  writeToStorage(window.sessionStorage, SESSION_ID_STORAGE_KEY, sessionId);
  return sessionId;
};

export const getTelemetryIdentity = (): TelemetryIdentity => ({
  anonId: resolveAnonId(),
  sessionId: resolveSessionId(),
  site: resolveSite(),
});

